import React from 'react';
import {
    Box,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormGroup,
    Checkbox,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserProfileData } from '../UserProfileForm';

const EXPERIENCE_LEVELS = ['Beginner', 'Intermediate', 'Expert'];
const GROWING_INTERESTS = ['Vegetables', 'Flowers', 'Herbs', 'Fruit'];

interface StepGrowPreferencesProps {
    data: Partial<UserProfileData>;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onInterestsChange: (interests: string[]) => void;
}

export const StepGrowPreferences: React.FC<StepGrowPreferencesProps> = ({ data, onChange, onInterestsChange }) => {
    const { t } = useTranslation();

    const handleInterestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const currentInterests = data.growingInterests || [];
        const newInterests = e.target.checked
            ? [...currentInterests, value]
            : currentInterests.filter(t => t !== value);
        onInterestsChange(newInterests);
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom sx={{ textAlign: 'left' }}>
                {t('onboarding.grow_preferences_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'left' }}>
                {t('onboarding.grow_preferences_subtitle')}
            </Typography>

            {/* Experience Level */}
            <Box sx={{ mb: 3 }}>
                <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ textAlign: 'left' }}>{t('settings.experience_level')}</FormLabel>
                    <RadioGroup
                        aria-label="experience-level"
                        name="experienceLevel"
                        value={data.experienceLevel || 'Beginner'}
                        onChange={onChange}
                        row
                        sx={{ mt: 1 }}
                    >
                        {EXPERIENCE_LEVELS.map(level => (
                            <FormControlLabel
                                key={level}
                                value={level}
                                control={<Radio />}
                                label={t(`settings.experience.${level.toLowerCase()}`)}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            </Box>

            {/* Growing Interests */}
            <Box sx={{ mb: 3 }}>
                <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ textAlign: 'left' }}>{t('settings.what_to_grow')}</FormLabel>
                    <FormGroup sx={{ flexDirection: 'row', mt: 1 }}>
                        {GROWING_INTERESTS.map(type => (
                            <FormControlLabel
                                key={type}
                                control={
                                    <Checkbox
                                        checked={(data.growingInterests || []).includes(type)}
                                        onChange={handleInterestChange}
                                        value={type}
                                    />
                                }
                                label={t(`settings.interests.${type.toLowerCase()}`)}
                                sx={{ width: '50%' }}
                            />
                        ))}
                    </FormGroup>
                </FormControl>
            </Box>

            <TextField
                label={t('settings.food_preferences')}
                name="foodPreferences"
                value={data.foodPreferences || ''}
                onChange={onChange}
                fullWidth
                multiline
                rows={3}
                margin="normal"
                helperText={t('settings.food_helper')}
            />
        </Box>
    );
};
